import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SmartPageRouter from './SmartPageRouter';
import Product from './flooring/Product';
import CollectionProductPage from './flooring/CollectionProductPage';

const CatchAllRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [showNotFound, setShowNotFound] = useState(false);
  const [showDynamicPage, setShowDynamicPage] = useState(false);
  const [showCategory, setShowCategory] = useState(false);
  const [showCollection, setShowCollection] = useState(false);

  useEffect(() => {
    console.log('=== CATCH ALL ROUTE ===');
    console.log('Current pathname:', location.pathname);
    
    const checkForRedirectAndPages = async () => {
      try {
        const currentPath = location.pathname;
        
        // Skip static files and known patterns that shouldn't be here
        if (currentPath.includes('.') || 
            currentPath.startsWith('/_') || 
            currentPath.startsWith('/static') ||
            currentPath.startsWith('/manifest.json') ||
            currentPath.startsWith('/favicon')) {
          setShowNotFound(true);
          setIsChecking(false);
          return;
        }
        
        const baseApiUrl = process.env.REACT_APP_API_URL || 'https://hdmovietoday.xyz';
        
        // First, check for redirects
        try {
          const response = await axios.get(
            `${baseApiUrl}/api/redirect-management/check-redirect`,
            {
              params: { url: currentPath },
              timeout: 5000
            }
          );

          if (response.data.success && response.data.redirect) {
            const { targetUrl } = response.data.redirect;
            
            if (targetUrl.startsWith('/')) {
              // Internal redirect - use React Router
              navigate(targetUrl, { replace: true });
              return;
            } else {
              // External redirect - use window.location
              window.location.href = targetUrl;
              return;
            }
          }
        } catch (redirectError) {
          // No redirect found, continue to check for categories and collections
        }
        
        // Extract slug from path
        const slug = currentPath.substring(1); // Remove leading slash
        
        if (slug) {
          // First, check if it's a category
          try {
            const categoryResponse = await axios.get(
              `${baseApiUrl}/api/category/slug/${slug}`,
              { timeout: 5000 }
            );
            
            if (categoryResponse.data && categoryResponse.data.data && categoryResponse.data.statusCode === 200) {
              console.log('Found category, showing Product component');
              setShowCategory(true);
              setIsChecking(false);
              return;
            } else {
              console.log('Category response does not match criteria:', categoryResponse.data);
            }
          } catch (categoryError) {
            // Not a category, continue to check collections
          }
          
          // Then, check if it's a collection
          try {
            const collectionResponse = await axios.get(
              `${baseApiUrl}/api/collections/${slug}`,
              { timeout: 5000 }
            );
            
            if (collectionResponse.data && collectionResponse.data.data && collectionResponse.data.statusCode === 200) {
              setShowCollection(true);
              setIsChecking(false);
              return;
            }
          } catch (collectionError) {
            // Not a collection, continue to check dynamic pages
          }
          
          // Then, check for dynamic pages (BlogManager or PageBuilder)
          // Check if it's a BlogManager page
          try {
            const blogResponse = await axios.get(
              `${baseApiUrl}/api/pages/${slug}`,
              { timeout: 5000 }
            );
            
            if (blogResponse.data) {
              setShowDynamicPage(true);
              setIsChecking(false);
              return;
            }
          } catch (blogError) {
            // Not a blog page, continue
          }
          
          // Check if it's a PageBuilder page
          try {
            const pageBuilderResponse = await axios.get(
              `${baseApiUrl}/api/page-templates/slug/${slug}`,
              { timeout: 5000 }
            );
            
            if (pageBuilderResponse.data && pageBuilderResponse.data.success) {
              setShowDynamicPage(true);
              setIsChecking(false);
              return;
            }
          } catch (pageBuilderError) {
            // Not a PageBuilder page either
          }
        }
        
        // If we get here, it's a 404
        setShowNotFound(true);
        
      } catch (error) {
        setShowNotFound(true);
      } finally {
        setIsChecking(false);
      }
    };

    checkForRedirectAndPages();
  }, [location.pathname, navigate]);

  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  if (showCategory) {
    console.log('Rendering Product component for category');
    return <Product />;
  }

  if (showCollection) {
    console.log('Rendering CollectionProductPage component for collection');
    return <CollectionProductPage />;
  }

  if (showDynamicPage) {
    return <SmartPageRouter />;
  }

  if (showNotFound) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h1 style={{ fontSize: '48px', marginBottom: '20px' }}>404</h1>
        <h2 style={{ fontSize: '24px', marginBottom: '20px' }}>Page Not Found</h2>
        <p style={{ fontSize: '16px', marginBottom: '30px', maxWidth: '600px' }}>
          The page you're looking for doesn't exist. It might have been moved, deleted, or you entered the wrong URL.
        </p>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button 
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Go Home
          </button>
          <button 
            onClick={() => navigate(-1)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default CatchAllRoute;
